import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Card, ErrorState, Loading, Metric, MetricRow, StatusPill, Table } from '../components.jsx'
import { useApi } from '../hooks.js'
import PageHeader from './PageHeader.jsx'

/**
 * The crane register, with statutory position in the same view.
 *
 * A supervisor's first question about a fleet is almost never "list the cranes" — it is "which ones must not be
 * operated", so lockout state and compliance risk are columns rather than a separate screen. The `?filter=`
 * values match the dashboard tiles, so clicking "Statutory overdue" lands on exactly those cranes.
 */
export default function Cranes() {
  const nav = useNavigate()
  const [params, setParams] = useSearchParams()
  const filter = params.get('filter') || 'all'
  const [q, setQ] = useState('')

  const assets = useApi('/assets')
  // Reliability carries downtime and the down-now flag per crane, so the register can rank by cost without a
  // second concept of "problem crane".
  const rel = useApi('/rcm/asset-reliability')

  const relById = useMemo(() => {
    const m = new Map()
    ;(rel.data || []).forEach((r) => m.set(r.assetId, r))
    return m
  }, [rel.data])

  const rows = useMemo(() => {
    let r = (assets.data || []).filter((a) => a.status !== 'DECOMMISSIONED')
    if (filter === 'down') r = r.filter((a) => a.doNotOperate || a.status === 'LOCKED_OUT' || a.status === 'UNDER_MAINTENANCE')
    else if (filter === 'locked') r = r.filter((a) => a.doNotOperate || a.status === 'LOCKED_OUT')
    if (q.trim()) {
      const t = q.trim().toLowerCase()
      r = r.filter((a) => [a.assetCode, a.name, a.department, a.area, a.bay]
        .some((v) => v && String(v).toLowerCase().includes(t)))
    }
    return r
  }, [assets.data, filter, q])

  const all = (assets.data || []).filter((a) => a.status !== 'DECOMMISSIONED')

  return (
    <>
      <PageHeader
        title="Cranes"
        subtitle="In-service fleet. Decommissioned machines are excluded from counts and availability."
        actions={
          <input
            aria-label="Search cranes"
            placeholder="Crane no, name, department…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ minWidth: 240 }}
          />
        }
      />

      {assets.loading && <Loading rows={8} />}
      {assets.error && <ErrorState error={assets.error} onRetry={assets.reload} />}

      {assets.data && (
        <>
          <MetricRow>
            <Metric label="In service" value={all.length} onClick={() => setParams({})} />
            <Metric
              label="Do not operate"
              value={all.filter((a) => a.doNotOperate || a.status === 'LOCKED_OUT').length}
              kind="safety"
              onClick={() => setParams({ filter: 'locked' })}
            />
            <Metric
              label="Under maintenance"
              value={all.filter((a) => a.status === 'UNDER_MAINTENANCE').length}
              kind="warning"
              onClick={() => setParams({ filter: 'down' })}
            />
            <Metric label="Departments" value={new Set(all.map((a) => a.department)).size} />
          </MetricRow>

          {filter !== 'all' && (
            <p className="muted" style={{ margin: 'var(--md) 0' }}>
              Filtered view · {rows.length} of {all.length}{' '}
              <button className="btn" onClick={() => setParams({})}>Clear</button>
            </p>
          )}

          <Card>
            <Table
              keyOf={(a) => a.id}
              onRowClick={(a) => nav(`/cranes/${a.id}`)}
              columns={[
                { key: 'assetCode', header: 'Crane no', render: (a) => <strong>{a.assetCode}</strong> },
                { key: 'name', header: 'Name' },
                {
                  key: 'where',
                  header: 'Location',
                  render: (a) => (
                    <span className="cell-stack">
                      <span>{a.department}</span>
                      <span className="muted">{[a.area, a.bay].filter(Boolean).join(' · ')}</span>
                    </span>
                  ),
                },
                {
                  key: 'status',
                  header: 'Status',
                  /*
                   * One pill, and the safety fact wins.
                   *
                   * The first version drew both `status` and a second pill for `doNotOperate`, which on
                   * CRANE-04 rendered "ACTIVE" and "LOCKED OUT" side by side — reported, correctly, as
                   * impossible. My comment there called it "the honest rendering"; two contradictory pills are
                   * not honest, they are unreadable, and on a safety flag that is the worst place to make a
                   * reader arbitrate.
                   *
                   * `do_not_operate` is the operational truth: it is what the operator's app blocks on and
                   * what LOTO sets. So when it is true the crane reads DO NOT OPERATE regardless of the status
                   * column, with the reason on hover, and the underlying status is shown as quiet secondary
                   * text rather than a competing badge. Nobody has to decide which pill to believe.
                   *
                   * The two fields disagreeing at all is a data defect — `AssetSafetyService.lock` always sets
                   * both together, so the code cannot produce this state; a dev seed row set the flag without
                   * the status, with a comment admitting it was doing the service layer's job by hand. Fixed
                   * separately. This rendering has to be right either way, because real data drifts.
                   */
                  render: (a) => (
                    <span className="cell-stack">
                      {a.doNotOperate
                        ? <StatusPill status="LOCKED_OUT" title={a.doNotOperateReason || 'Do not operate'} />
                        : <StatusPill status={a.status} />}
                      {a.doNotOperate && a.status !== 'LOCKED_OUT' && (
                        <span className="muted" title={`Status column reads ${a.status}`}>
                          {title(a.status)} · isolated
                        </span>
                      )}
                    </span>
                  ),
                },
                {
                  key: 'failures',
                  header: 'Failures (90d)',
                  numeric: true,
                  render: (a) => relById.get(a.id)?.failures ?? <span className="muted">—</span>,
                },
                {
                  key: 'avail',
                  header: 'Availability',
                  numeric: true,
                  render: (a) => {
                    const r = relById.get(a.id)
                    if (!r) return <span className="muted">—</span>
                    return <span className={r.availabilityPct >= 95 ? 'val-good' : r.availabilityPct >= 85 ? 'val-warn' : 'val-bad'}>{r.availabilityPct.toFixed(1)}%</span>
                  },
                },
                { key: 'installed', header: 'Installed', render: (a) => a.installDate || <span className="muted">—</span> },
              ]}
              rows={rows}
              empty={<p className="muted">No cranes match. If the fleet is empty, import the master data first.</p>}
            />
          </Card>
        </>
      )}
    </>
  )
}

/** ACTIVE -> "Active", UNDER_MAINTENANCE -> "Under maintenance". */
const title = (s) => (s ? s.split('_').map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(' ') : '—')
